var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { createSelector } from '@reduxjs/toolkit';
import { languages } from 'countries-list';
import { keyBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Language } from '../../../enum';
import { useAPI, useAuthenticatedAPI, useCachedAPI } from '../../../state';
import { toInt } from '../../../util';
import { compareQueueNames, getLOBName, getUnassignedLOB, sortByLOBName } from '../../common';
import { useCancelSimulation } from '../../report/cancelSimulation';
import { STATUS_BY_STEP, getStepsLength, getWorkflowSteps } from '../constants';
import { useDeleteAllMultiskillAPI, usePlanAPI, usePlanTypeCategory, useWizardParams } from '../selectors';
import { Completed, } from './types';
var WORK_TYPE_API = '/api/worktype';
export var QUEUE_LIST_API = '/api/queue/findAll/{planId}';
var QUEUES_API = '/api/queue';
var QUEUE_API = '/api/queue/{id}';
var QUEUE_BULK_DELETE_API = '/api/queue/bulk';
var LOBS_LIST_API = '/api/queue/clientsByOrg/{clientId}/lobs';
export var selectQueues = function (queues) { return queues; };
export var selectQueueMap = createSelector(selectQueues, function (queues) { return keyBy(queues, 'id'); });
// Sentinel id for the "+ Add New" LOB / Sub-LOB option, shared by the Add/Edit Queue dialog and the
// inline Queues-table cells so the two surfaces stay interchangeable. Real LOB ids start at 1.
export var NEW_LOB_OPTION_ID = 0;
// Max length for a queue name and a sandbox LOB / Sub-LOB name.
export var QUEUE_NAME_MAX_LENGTH = 24;
export var useWorkTypeAPI = function () {
    return useCachedAPI(WORK_TYPE_API);
};
export var useQueuesAPI = function () {
    var planId = useParams().id;
    var _a = useAPI(QUEUE_LIST_API, { planId: planId }), data = _a[0], fetchData = _a[1], clearData = _a[2];
    var fetchDataWrapper = useCallback(function () {
        if (planId) {
            fetchData();
        }
    }, [fetchData, planId]);
    return [data, fetchDataWrapper, clearData];
};
export var useLobsAPI = function () {
    var _a, _b;
    var planData = usePlanAPI()[0];
    var clientId = (_a = planData.data) === null || _a === void 0 ? void 0 : _a.clientId;
    var planId = (_b = planData.data) === null || _b === void 0 ? void 0 : _b.basePlanId;
    var _c = useAPI(LOBS_LIST_API, { clientId: clientId }), data = _c[0], fetchData = _c[1], clearData = _c[2];
    var fetchDataWrapper = useCallback(function () {
        if (clientId && planId) {
            fetchData(undefined, { planId: planId });
        }
    }, [clientId, fetchData, planId]);
    return [data, fetchDataWrapper, clearData];
};
export var useCreateQueueAPI = function () {
    return useAuthenticatedAPI(QUEUES_API, undefined, 'POST');
};
export var useDeleteQueueAPI = function (id) {
    return useAuthenticatedAPI(QUEUE_API, { id: id }, 'DELETE');
};
export var useBulkDeleteQueueAPI = function () {
    return useAuthenticatedAPI(QUEUE_BULK_DELETE_API, undefined, 'DELETE');
};
export var useRenameQueueAPI = function (id) {
    return useAuthenticatedAPI(QUEUE_API, { id: id }, 'PUT');
};
export var useUpdateQueueAPI = function (id) {
    return useAuthenticatedAPI(QUEUE_API, { id: id }, 'PUT');
};
export var useSelectedQueue = function (id) {
    var queueId = useWizardParams().queueId;
    id = id !== null && id !== void 0 ? id : queueId;
    var queueData = useQueuesAPI()[0];
    var queueMap = selectQueueMap(queueData.data);
    return id ? queueMap[id] : undefined;
};
export var useQueueStatus = function (id) {
    var _a;
    var status = ((_a = useSelectedQueue(id)) !== null && _a !== void 0 ? _a : {}).status;
    return status;
};
export var useCurrentQueueCompletedStep = function () {
    var queueId = useWizardParams().queueId;
    var queueStatus = useQueueStatus(queueId);
    var planTypeCategory = usePlanTypeCategory();
    var stepsLength = getStepsLength(planTypeCategory);
    var workflowSteps = getWorkflowSteps(planTypeCategory);
    return useMemo(function () {
        var _a;
        if (!queueStatus || !queueId) {
            return [-1];
        }
        for (var stepIndex = 0; stepIndex < stepsLength; stepIndex++) {
            var step = workflowSteps[stepIndex];
            var incompleteType = (_a = STATUS_BY_STEP[step]) === null || _a === void 0 ? void 0 : _a.find(function (type) { return queueStatus[type] === Completed.No; });
            if (incompleteType) {
                return [step, incompleteType];
            }
        }
        var lastStep = workflowSteps[stepsLength - 1];
        return [lastStep];
    }, [queueStatus, queueId, stepsLength, workflowSteps]);
};
export var usePlanStatus = function (type) {
    var _a;
    var queuesData = useQueuesAPI()[0];
    return (_a = queuesData.data) === null || _a === void 0 ? void 0 : _a.some(function (_a) {
        var status = _a.status;
        return status[type];
    });
};
export var useQueueIncomplete = function (id, step) {
    var status = useQueueStatus(id);
    var planTypeCategory = usePlanTypeCategory();
    var workflowSteps = getWorkflowSteps(planTypeCategory);
    return useMemo(function () {
        var _a;
        if (!status)
            return false;
        // If a specific step is provided, only check that step's status types
        if (step !== undefined) {
            var statusTypes = STATUS_BY_STEP[step];
            return (_a = statusTypes === null || statusTypes === void 0 ? void 0 : statusTypes.some(function (type) { return status[type] === Completed.No; })) !== null && _a !== void 0 ? _a : false;
        }
        // Otherwise, check if queue is incomplete on any step (original behavior)
        return workflowSteps.some(function (workflowStep) {
            var statusTypes = STATUS_BY_STEP[workflowStep];
            return statusTypes === null || statusTypes === void 0 ? void 0 : statusTypes.some(function (type) { return status[type] === Completed.No; });
        });
    }, [status, workflowSteps, step]);
};
export var useCompletedStep = function () {
    var queuesData = useQueuesAPI()[0];
    var planTypeCategory = usePlanTypeCategory();
    var stepsLength = getStepsLength(planTypeCategory);
    var workflowSteps = getWorkflowSteps(planTypeCategory);
    return useMemo(function () {
        var _a;
        if (!queuesData.data) {
            return [-1];
        }
        if (!queuesData.data.length) {
            return [workflowSteps[0]];
        }
        for (var stepIndex = 0; stepIndex < stepsLength; stepIndex++) {
            var step = workflowSteps[stepIndex];
            var incompleteType = (_a = STATUS_BY_STEP[step]) === null || _a === void 0 ? void 0 : _a.find(function (type) { var _a; return (_a = queuesData.data) === null || _a === void 0 ? void 0 : _a.some(function (_a) {
                var status = _a.status;
                return status[type] === Completed.No;
            }); });
            if (incompleteType) {
                return [step, incompleteType];
            }
        }
        var lastStep = workflowSteps[stepsLength - 1];
        return [lastStep];
    }, [queuesData.data, stepsLength, workflowSteps]);
};
export var useQueuesCopyFrom = function (statusType, getOptions) {
    var queuesData = useQueuesAPI()[0];
    var queueId = useWizardParams().queueId;
    var unassignedLOB = getUnassignedLOB();
    return useMemo(function () {
        if (!queuesData.data)
            return [];
        if (getOptions)
            return getOptions(queuesData.data);
        return queuesData.data
            .filter(function (_a) {
            var id = _a.id, status = _a.status;
            return id !== queueId && status[statusType] !== Completed.No;
        })
            .map(function (_a) {
            var id = _a.id, name = _a.name, lobName = _a.lobName, subLobName = _a.subLobName;
            return ({
                id: id.toString(),
                name: name,
                lobName: getLOBName(lobName, subLobName, unassignedLOB),
            });
        })
            .sort(function (a, b) { return sortByLOBName(a, b, unassignedLOB); });
    }, [getOptions, queueId, queuesData.data, statusType, unassignedLOB]);
};
export var useQueueAPI = function () {
    var queueId = useWizardParams().queueId;
    var _a = useAPI(QUEUE_API, { id: queueId }), data = _a[0], fetchData = _a[1], clearData = _a[2];
    var fetchDataWrapper = useCallback(function () {
        if (queueId) {
            fetchData();
        }
    }, [fetchData, queueId]);
    return [data, fetchDataWrapper, clearData];
};
export var useQueueByIdAPI = function (queueId) {
    var _a = useAPI(QUEUE_API, { id: queueId }), data = _a[0], fetchData = _a[1], clearData = _a[2];
    var fetchDataWrapper = useCallback(function () {
        if (queueId) {
            fetchData();
        }
    }, [fetchData, queueId]);
    return [data, fetchDataWrapper, clearData];
};
export var useRefreshQueueData = function () {
    var _a = useQueueAPI(), fetchQueue = _a[1];
    var _b = useQueuesAPI(), fetchQueues = _b[1];
    return useCallback(function () {
        fetchQueue();
        fetchQueues();
    }, [fetchQueue, fetchQueues]);
};
export var useSaveQueueAPI = function (type) {
    if (type === void 0) { type = ''; }
    return useAuthenticatedAPI(QUEUES_API, undefined, 'PATCH', { key: "".concat(QUEUES_API, ":GET:").concat(type) });
};
export var useQueueHasMultiskill = function () {
    var _a, _b;
    var queuesData = useQueuesAPI()[0];
    var queueData = useQueueAPI()[0];
    return (_b = (_a = queuesData.data) === null || _a === void 0 ? void 0 : _a.find(function (d) { var _a; return d.id === ((_a = queueData.data) === null || _a === void 0 ? void 0 : _a.id); })) === null || _b === void 0 ? void 0 : _b.hasMultiskill;
};
export var useQueuesHasMultiskill = function () {
    var _a;
    var queuesData = useQueuesAPI()[0];
    return (_a = queuesData.data) === null || _a === void 0 ? void 0 : _a.some(function (d) { return d.hasMultiskill; });
};
export var useQueueLocation = function () {
    var _a, _b;
    var queueData = useQueueAPI()[0];
    return (_b = (_a = queueData.data) === null || _a === void 0 ? void 0 : _a.queueLocations) !== null && _b !== void 0 ? _b : [];
};
export var useCancelAndDeleteMultiskill = function () {
    var _a = useParams(), basePlanId = _a.basePlanId, id = _a.id;
    var _b = useDeleteAllMultiskillAPI(), fetchDeleteAllMultiskillData = _b[1];
    var _c = useCancelSimulation(toInt(id), toInt(basePlanId)), cancelSimulation = _c[1];
    return useCallback(function () {
        if (basePlanId && id) {
            cancelSimulation({ isCancelAll: true });
            fetchDeleteAllMultiskillData();
        }
    }, [basePlanId, cancelSimulation, fetchDeleteAllMultiskillData, id]);
};
export var sortQueuesByLob = function (queues, unassignedLOB) {
    if (unassignedLOB === void 0) { unassignedLOB = getUnassignedLOB(); }
    return queues
        .slice()
        .sort(function (a, b) {
        return sortByLOBName({ lobName: getLOBName(a.lobName, a.subLobName, unassignedLOB), name: a.businessArea }, { lobName: getLOBName(b.lobName, b.subLobName, unassignedLOB), name: b.businessArea }, unassignedLOB);
    });
};
export var useGroupedQueues = function (queues) {
    var t = useTranslation('wizard', { keyPrefix: 'queue' }).t;
    var unassignedLOB = t('queuePanel.unassignedLOB');
    return useMemo(function () {
        if (!(queues === null || queues === void 0 ? void 0 : queues.length))
            return [];
        var grouped = Object.values(queues.reduce(function (acc, queue) {
            var _a;
            var label = [queue.lobName, queue.subLobName].filter(Boolean).join(': ') || unassignedLOB;
            (_a = acc[label]) !== null && _a !== void 0 ? _a : (acc[label] = {
                label: label,
                queues: [],
            });
            acc[label].queues.push(queue);
            return acc;
        }, {}));
        return grouped
            .map(function (group) { return (__assign(__assign({}, group), { queues: group.queues.slice().sort(function (a, b) { return compareQueueNames(a.name, b.name); }) })); })
            .slice()
            .sort(function (a, b) {
            return sortByLOBName({ name: a.label, lobName: a.label }, { name: b.label, lobName: b.label }, unassignedLOB);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queues]);
};
export var useLanguageOptions = function () {
    var _a;
    var i18n = useTranslation().i18n;
    var locale = (_a = i18n.resolvedLanguage) !== null && _a !== void 0 ? _a : Language.EN;
    return useMemo(function () {
        var displayNames = new Intl.DisplayNames([locale], {
            type: 'language',
        });
        return Object.keys(languages)
            .map(function (code) {
            var language = languages[code];
            var displayName = displayNames.of(code);
            var fallbackName = code;
            if (typeof language === 'string') {
                fallbackName = language;
            }
            else if (typeof language.name === 'string') {
                fallbackName = language.name;
            }
            return {
                code: code,
                name: displayName && displayName.toLowerCase() !== code
                    ? displayName.charAt(0).toLocaleUpperCase(locale) + displayName.slice(1)
                    : fallbackName,
            };
        })
            .filter(function (item, index, arr) { return arr.findIndex(function (x) { return x.name === item.name; }) === index; })
            .sort(function (a, b) { return a.name.localeCompare(b.name, locale); });
    }, [locale]);
};
